import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import BookAppointmentLink from './BookAppointmentLink';
import { trackPhoneClick, trackEmailClick } from '../utils/analytics';

const Navigation: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const menuItems = [
    { name: 'Services', path: '/services', hasDropdown: true },
    { name: 'Research', path: '/research', hasDropdown: true },
    { name: 'Our Team', path: '/team', hasDropdown: true },
    { name: 'Referrals', path: '/referrals', hasDropdown: false },
    { name: 'Contact', path: '/contact', hasDropdown: true }
  ];

  const isActiveLink = (path: string) => {
    return location.pathname === path;
  };

  return (
    <nav className="shadow-sm sticky top-0 z-50 bg-transparent lg:bg-white lg:border-b lg:border-gray-100">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile sticky bar */}
        <div className="lg:hidden pt-3 pb-3">
          <div className="mx-3 flex items-center justify-between bg-white/95 backdrop-blur supports-[backdrop-filter]:backdrop-blur shadow-md px-3 py-2 rounded-2xl border border-hellotend-neutral-200">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity duration-300">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center p-1">
                <img 
                  src="/Chomp_Logo_Black.png" 
                  alt="CHOMP Pet Dental — home, mobile header" 
                  loading="lazy"
                  width="64"
                  height="64"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="hidden md:block text-2xl text-gray-800 uppercase tracking-tight" style={{ fontFamily: 'League Spartan, sans-serif', fontWeight: 600 }}>
                CHOMP
              </div>
            </Link>
            <div className="flex items-center gap-2 sm:gap-3">
              <a
                href="tel:+19544663775"
                onClick={() => trackPhoneClick('mobile_nav_top')}
                className="inline-flex items-center gap-1.5 text-brand-teal hover:text-teal-700 transition-colors duration-300 text-xs sm:text-sm font-bold whitespace-nowrap px-1 py-2"
                aria-label="Call CHOMP at (954) 466-3775"
              >
                <i className="fas fa-phone" aria-hidden="true"></i>
                <span>(954) 466-3775</span>
              </a>
              <BookAppointmentLink
                analyticsSource="mobile_nav"
                className="bg-hellotend-brand-blue text-white px-4 sm:px-6 py-2.5 font-bold text-sm tracking-wide uppercase hover:bg-blue-700 transition-colors duration-300 shadow-sm"
                style={{borderRadius: '2rem'}}
              >
                Book
              </BookAppointmentLink>
              <button
                type="button"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-hellotend-neutral-800 hover:text-hellotend-neutral-700 transition-colors duration-300 p-1"
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMenuOpen}
                aria-controls="mobile-primary-menu"
              >
                <i className={`fas ${isMenuOpen ? 'fa-times' : 'fa-bars'} text-xl`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Desktop row */}
        <div className="hidden lg:flex items-center justify-between h-16 sm:h-18 lg:h-24">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-3 sm:gap-4 hover:opacity-80 transition-opacity duration-300">
              <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-xl flex items-center justify-center p-1">
                <img 
                  src="/Chomp_Logo_Black.png" 
                  alt="CHOMP Pet Dental — home, desktop header" 
                  loading="lazy"
                  width="128"
                  height="128"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="text-2xl sm:text-3xl lg:text-4xl text-gray-800 uppercase whitespace-nowrap" style={{ fontFamily: 'League Spartan, sans-serif', fontWeight: 600 }}>
                CHOMP PET DENTAL
              </div>
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8 xl:space-x-12">
            {menuItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`flex items-center gap-1 text-hellotend-neutral-800 hover:text-hellotend-neutral-800 transition-colors duration-300 font-founders text-base whitespace-nowrap ${
                  isActiveLink(item.path) ? 'text-hellotend-neutral-800 border-b-2 border-hellotend-neutral-800 pb-1' : ''
                }`}
                style={{fontWeight: 400}}
              >
                {item.name}
                {item.hasDropdown && (
                  <i className="fas fa-chevron-down text-xs ml-1"></i>
                )}
              </Link>
            ))}
          </div>

          {/* Desktop CTA Button */}
          <div className="hidden lg:flex items-center space-x-4 flex-shrink-0 ml-8">
            <BookAppointmentLink
              analyticsSource="nav"
              className="bg-hellotend-brand-blue text-white px-8 py-3.5 font-medium font-founders hover:bg-blue-700 transition-all duration-300 text-base whitespace-nowrap"
              style={{borderRadius: '1.75rem'}}
            >
              Book now
            </BookAppointmentLink>
          </div>
        </div>

        {/* Mobile Menu
            When closed, use `inert` instead of `aria-hidden`. `inert` removes
            the subtree from the focus order AND from assistive tech, which
            matches the visual hidden state. `aria-hidden` alone leaves the
            links keyboard-focusable while invisible — a real a11y bug. */}
        <div
          id="mobile-primary-menu"
          className={`lg:hidden transition-all duration-300 ease-in-out ${
          isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        } overflow-hidden`}
          {...(!isMenuOpen ? { inert: '' as any } : {})}
        >
          <div className="py-6 space-y-6 border-t border-hellotend-neutral-200 bg-white">
            {menuItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block px-6 py-3 text-hellotend-neutral-700 hover:text-hellotend-neutral-800 hover:bg-hellotend-neutral-100 transition-all duration-300 rounded-xl font-medium font-founders text-lg ${
                  isActiveLink(item.path) ? 'text-hellotend-neutral-800 bg-hellotend-neutral-100' : ''
                }`}
              >
                {item.name}
              </Link>
            ))}
            <div className="px-6 pt-6 border-t border-hellotend-neutral-200">
              <a 
                href="mailto:info@chomp.vet"
                onClick={() => trackEmailClick('mobile_nav', 'info@chomp.vet')}
                className="flex items-center text-hellotend-neutral-700 hover:text-hellotend-neutral-800 transition-colors duration-300 py-3 font-founders text-base"
              >
                <i className="fas fa-envelope mr-4 text-lg"></i>
                info@chomp.vet
              </a>
              <a 
                href="tel:+19544663775" 
                onClick={() => trackPhoneClick('mobile_nav')}
                className="flex items-center text-hellotend-neutral-700 hover:text-hellotend-neutral-800 transition-colors duration-300 py-3 font-founders text-base"
              >
                <i className="fas fa-phone mr-4 text-lg"></i>
                (954) 466-3775
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;